import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_GRAY_BACKGROUND,
  BASIC_LINE,
  BASIC_WHITE,
} from "../../../constants/colors";
import { useCart } from "../../../hooks/useCart";
import { useCoupon } from "../../../hooks/useCoupon";
import { Button } from "../../atoms/Button";
import { CouponInput } from "../../molecules/menu/CouponInput";
import { StoreDetail } from "../../molecules/store/StoreDetail";
import { MemoInput } from "../../organisms/order/MemoInput";
import { OrderContent } from "../../organisms/order/OrderContent";
import { OrderItem } from "../../organisms/order/OrderItem";
import { PaymentSelection } from "../../organisms/order/PaymentSelection";
import { PaymentMethod, useOrderConfirmation } from "./hook";
import { CampaignManager } from "../../../core/storage/CampaignManager";

type Props = {
  showAppCouponHeader: boolean;
};

export const OrderConfirmation: React.FC<Props> = ({ showAppCouponHeader }) => {
  const useCouponHook = useCoupon();
  const {
    appliedCoupons,
    applyCoupon,
    getCouponName,
    handleRemoveCoupon,
    handleUseAppCoupon,
  } = useCouponHook;

  const {
    store,
    date,
    menuMapById,
    handleSetFavoriteStore,
    pickupDateText,
    recommendedMenus,
    limitedMenus,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    handleSetFavoriteMenu,
    handleBackToMenu,
    handlePlaceOrder,
    handlePayment,
  } = useOrderConfirmation(useCouponHook);

  const {
    menusInCart,
    orderedMenus,
    selectedLimitedMenus,
    totalItemsInCart,
    totalPriceInCart,
    totalTaxInCart,
    orderCoupon,
    taxDetails,
    shouldAnimate,
    handleUpdateItem,
    handleRemoveItem,
  } = useCart(
    store,
    useCouponHook,
    menuMapById,
    recommendedMenus,
    limitedMenus,
  );

  const isStockItemMode = CampaignManager.isStockItemMode();

  const getUniqueKey = (menuId: number, optionId?: number) =>
    `${Math.random().toString(36).slice(-8)}-${menuId}-${optionId ?? "none"}`;

  return (
    <OrderConfirmationContainer $hasAppCoupon={showAppCouponHeader}>
      <OrderConfirmationTitle>ご注文内容確認</OrderConfirmationTitle>

      {store && (
        <StoreDetailContainer>
          <StoreDetail
            id={store.id}
            name={store.name}
            address={store.prefecture + store.city + store.address}
            phoneNumber={store.phoneNumber}
            service={store.service}
            location={{
              lat: store.location.latitude,
              lng: store.location.longitude,
            }}
            isAdvancedPayment={store.isPaymentAvailable}
            isFavorite={store.isFavorite}
            onFavoriteClick={handleSetFavoriteStore}
            pickupDateText={pickupDateText}
          />
        </StoreDetailContainer>
      )}

      <SelectedItemsContainer>
        <SelectedItemsTitle>選択した商品</SelectedItemsTitle>

        {menuMapById &&
          menusInCart &&
          menusInCart.map((menuInCart, index) => {
            const menu = menuMapById.get(menuInCart.menuId);

            if (!menu) return <div key={index}></div>;

            return (
              <OrderItem
                key={getUniqueKey(menu.id, menuInCart.optionId)}
                menu={menu}
                menuInCart={menuInCart}
                selectedLimitedMenus={selectedLimitedMenus}
                animate={shouldAnimate}
                onFavorite={() =>
                  handleSetFavoriteMenu(menu.id, menu.code, !menu.isFavorite)
                }
                onUpdateItem={(...args) => handleUpdateItem(index, ...args)}
                onRemove={() => handleRemoveItem(index)}
              />
            );
          })}

        <ButtonContainer>
          <Button
            btnStyle="secondary"
            text="他の商品を追加"
            onClick={handleBackToMenu}
            rounded="md"
          />
        </ButtonContainer>
      </SelectedItemsContainer>

      {store && date && totalItemsInCart > 0 && (
        <CouponContainer>
          <CouponInput
            onUseAppCoupon={() =>
              handleUseAppCoupon({
                storeId: store.id,
                pickupDate: date,
                storeShortestTimeUntilPickup: store.shortestTimeUntilPickup,
                redirectUrl: window.location.href,
              })
            }
            onApplyCoupon={(couponCode) =>
              applyCoupon(store.id, date, store.shortestTimeUntilPickup, [
                couponCode,
              ])
            }
            onRemoveCoupon={handleRemoveCoupon}
            getCouponName={getCouponName}
            appliedCoupons={appliedCoupons}
          />
        </CouponContainer>
      )}

      <OrderContentContainer>
        <OrderContent
          menus={orderedMenus}
          hideMemo
          totalPrice={totalPriceInCart}
          totalQuantity={totalItemsInCart}
          taxAmount={totalTaxInCart}
          coupon={orderCoupon}
          taxDetails={taxDetails}
        />
      </OrderContentContainer>

      {totalItemsInCart > 0 && (
        <MemoContainer>
          <MemoInput />
        </MemoContainer>
      )}

      {store &&
        store.isPaymentAvailable &&
        totalPriceInCart > 0 &&
        !isStockItemMode && (
          <PaymentSelection
            selectedPaymentMethod={selectedPaymentMethod}
            onChangePaymentMethod={setSelectedPaymentMethod}
            isDisabled={totalItemsInCart === 0}
          />
        )}

      {isStockItemMode && (
        <StockItemAlert>
          <StockItemAlertText>
            お客様のご都合によるキャンセルはできませんのでご了承ください
          </StockItemAlertText>
        </StockItemAlert>
      )}

      <ButtonWrapper>
        {((store?.isPaymentAvailable &&
          selectedPaymentMethod === PaymentMethod.AdvancedPayment) ||
          isStockItemMode) && (
          <Button
            btnStyle="primary"
            text="ご注文決済手続きを行う"
            onClick={handlePayment}
            disabled={totalItemsInCart === 0}
            rounded="md"
          />
        )}

        {(!store?.isPaymentAvailable ||
          selectedPaymentMethod === PaymentMethod.StorePayment ||
          totalPriceInCart === 0) && (
          <Button
            btnStyle="primary"
            text="ご注文を確定"
            onClick={handlePlaceOrder}
            disabled={totalItemsInCart === 0}
            rounded="md"
          />
        )}

        <Button
          btnStyle="secondary"
          text="メニューに戻る"
          onClick={handleBackToMenu}
          rounded="md"
        />
      </ButtonWrapper>
    </OrderConfirmationContainer>
  );
};

const OrderConfirmationContainer = styled.div<{ $hasAppCoupon: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $hasAppCoupon }) => $hasAppCoupon && "margin-top: 100px;"}
  background-color: ${BASIC_WHITE};
  border-radius: 8px;
  text-align: center;
  color: ${BASIC_DARK_GRAY};
`;

const OrderConfirmationTitle = styled.div`
  font-size: 20px;
  font-weight: 200;
  line-height: 1em;
  padding: 33px 0 29px 0;
  display: flex;
  justify-content: center;
`;

const StoreDetailContainer = styled.div`
  padding: 18px 18px 0;
  background-color: ${BASIC_GRAY_BACKGROUND};
  margin-bottom: 60px;
  color: ${BASIC_DARK_GRAY};
`;

const SelectedItemsContainer = styled.div`
  margin: 0 16px 55px;
  color: ${BASIC_DARK_GRAY};
`;

const SelectedItemsTitle = styled.div`
  text-align: left;
  margin: 0 0 30px;
  line-height: 1em;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid ${BASIC_LINE};
`;

const CouponContainer = styled.div`
  margin: 0 16px;
`;

const OrderContentContainer = styled.div`
  padding: 0 16px;
`;

const MemoContainer = styled.div`
  margin-top: 30px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 35px 0 50px;
  gap: 35px;
  button {
    height: auto;
    line-height: 1em;
    font-weight: 600;
  }
`;

const ButtonContainer = styled.div`
  button {
    height: auto;
    line-height: 1em;
    margin: 25px 0 0;
    font-weight: 600;
  }
`;

const StockItemAlert = styled.div`
  padding: 20px 20px;
  margin: 0 15px 10px 15px;
  border-radius: 8px;
  background-color: #e66231;
  text-align: center;
`;

const StockItemAlertText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #ffffff;
`;
